import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CreditCard, Zap, ArrowRight, CheckCircle, Lock, Loader2, Crown, Sparkles, ShieldCheck, AlertTriangle, ExternalLink } from "lucide-react";

const plans = [
  {
    id: "standard",
    name: "Standard",
    price: 4,
    credits: 50,
    features: ["AI Review Replies", "Basic Analytics", "1 Business Profile", "Email support"],
  },
  {
    id: "pro",
    name: "Pro",
    price: 10,
    credits: 200,
    highlighted: true,
    features: [
      "Everything in Standard",
      "Advanced Analytics",
      "Unlimited Business Profiles",
      "Priority AI response speed",
      "Export reviews",
    ],
  },
];

export default function Billing() {
  const { user } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [diag, setDiag] = useState(null);
  const currentPlan = user.plan;
  const resetDate = new Date(user.credits_reset_at).toLocaleDateString();

  useEffect(() => {
    api.get("/billing/diagnostics").then(({ data }) => setDiag(data)).catch(() => setDiag({ account_ok: false, errors: ["Diagnostics unavailable"] }));
  }, []);

  const subscribe = async (planId) => {
    setLoadingPlan(planId);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: origin,
      });
      // Redirect directly to Stripe Checkout
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoadingPlan(null);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl" data-testid="billing-page">
      <div>
        <h1 className="text-3xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Billing</h1>
        <p className="text-sm text-zinc-400 mt-1">Manage your plan and credits. Secure payments powered by Stripe.</p>
      </div>

      {/* Stripe connection status */}
      <div className={`glass p-5 flex items-start gap-4 ${diag?.account_ok && diag?.charges_enabled ? "" : diag ? "border-amber-500/30" : ""}`} data-testid="stripe-status-card">
        <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${diag?.account_ok && diag?.charges_enabled ? "bg-emerald-500/10 text-emerald-400" : diag ? "bg-amber-500/10 text-amber-400" : "bg-white/5 text-zinc-400"}`}>
          {diag?.account_ok && diag?.charges_enabled ? <ShieldCheck size={18} /> : diag ? <AlertTriangle size={18} /> : <Loader2 size={18} className="animate-spin" />}
        </div>
        <div className="flex-1 min-w-0">
          {!diag ? (
            <div className="text-sm text-zinc-400">Checking Stripe connection…</div>
          ) : diag.account_ok && diag.charges_enabled ? (
            <>
              <div className="text-sm font-medium">Stripe connected — ready to accept payments</div>
              <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                <span>Account: <span className="text-zinc-300">{diag.account_name}</span></span>
                <span>Mode: <span className={diag.mode === "live" ? "text-emerald-400" : "text-amber-400"}>{diag.mode}</span></span>
                <span>Country: <span className="text-zinc-300">{diag.country}</span></span>
                <span>Payouts: <span className={diag.payouts_enabled ? "text-emerald-400" : "text-amber-400"}>{diag.payouts_enabled ? "enabled" : "pending"}</span></span>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium">Stripe account needs attention</div>
              <ul className="text-xs text-zinc-400 mt-1 list-disc list-inside space-y-0.5">
                {(diag.errors || []).map((err, i) => (<li key={i}>{err}</li>))}
                {!diag.errors?.length && !diag.account_ok && <li>Could not reach Stripe. Check your API key.</li>}
                {diag.account_ok && !diag.charges_enabled && <li>Your Stripe account cannot accept charges yet — complete onboarding in Stripe.</li>}
              </ul>
              <a href="https://dashboard.stripe.com/settings/account" target="_blank" rel="noreferrer" className="text-xs text-[#FF2D75] hover:underline inline-flex items-center gap-1 mt-2">
                Open Stripe dashboard <ExternalLink size={10} />
              </a>
            </>
          )}
        </div>
      </div>

      {/* Current plan overview */}
      <div className="glass p-8 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#FF2D75]/20 blur-3xl" />
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF2D75] mb-3">
          <CreditCard size={14} /> Current plan
        </div>
        <div className="flex items-end gap-3 mb-6 flex-wrap">
          <div className="text-5xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {currentPlan === "pro" ? "Pro" : currentPlan === "standard" ? "Standard" : "Free Trial"}
          </div>
          {currentPlan === "pro" && (
            <div className="px-2.5 py-1 rounded-full text-xs bg-gradient-to-r from-[#FF2D75] to-[#FF0055] flex items-center gap-1">
              <Crown size={12} /> Most Popular
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
            <div className="text-xs text-zinc-500 mb-1">Credits remaining</div>
            <div className="text-2xl font-semibold flex items-center gap-2">
              <Zap size={18} className="text-[#FF2D75]" />{user.credits}
            </div>
          </div>
          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
            <div className="text-xs text-zinc-500 mb-1">Next credit refresh</div>
            <div className="text-2xl font-semibold">{resetDate}</div>
          </div>
        </div>
      </div>

      {/* Subscribe plans */}
      <div>
        <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {currentPlan === "pro" ? "Switch plan" : "Upgrade your plan"}
        </h2>
        <p className="text-sm text-zinc-400 mb-6">Click a plan to open Stripe Checkout. You'll be redirected back after payment.</p>

        <div className="grid md:grid-cols-2 gap-5">
          {plans.map((p) => {
            const isCurrent = currentPlan === p.id;
            return (
              <div
                key={p.id}
                className={`relative glass p-7 flex flex-col ${p.highlighted ? "border-[#FF2D75]/40 glow-pink" : ""}`}
                data-testid={`billing-plan-${p.id}`}
              >
                {p.highlighted && (
                  <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-gradient-to-r from-[#FF2D75] to-[#FF0055] text-xs font-medium flex items-center gap-1">
                    <Crown size={12} /> Most Popular
                  </div>
                )}
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className="text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.name}</h3>
                  {isCurrent && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                      Current
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-semibold gradient-text">${p.price}</span>
                  <span className="text-zinc-500 text-sm">/ week</span>
                </div>
                <div className="text-xs text-zinc-400 mb-5">{p.credits} AI credits / week</div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle size={14} className="text-[#FF2D75] mt-0.5 shrink-0" />
                      <span className="text-zinc-300">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => subscribe(p.id)}
                  disabled={isCurrent || loadingPlan === p.id}
                  className={`w-full justify-center ${p.highlighted ? "btn-primary" : "inline-flex items-center gap-2 rounded-full px-6 py-3 bg-white/5 border border-white/10 hover:bg-white/10 transition text-sm font-medium"} ${isCurrent ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid={`subscribe-${p.id}-btn`}
                >
                  {loadingPlan === p.id ? (
                    <><Loader2 size={16} className="animate-spin" /> Redirecting to Stripe…</>
                  ) : isCurrent ? (
                    <><CheckCircle size={16} /> Currently subscribed</>
                  ) : (
                    <><Sparkles size={16} /> Subscribe to {p.name} <ArrowRight size={14} /></>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex items-center gap-2 text-xs text-zinc-500">
          <Lock size={12} /> Payments secured by Stripe. Cancel anytime.
        </div>
      </div>

      {/* What you get */}
      <div className="glass p-7">
        <h3 className="text-lg font-semibold mb-4">Every plan includes</h3>
        <ul className="grid md:grid-cols-2 gap-3 text-sm text-zinc-300">
          {["Unlimited reviews in your inbox", "AI-generated replies in 3 tones", "Reputation analytics dashboard", "Weekly credit refresh", "Public Wall of Love + QR code", "Cancel anytime"].map((f) => (
            <li key={f} className="flex items-start gap-3">
              <CheckCircle size={16} className="text-[#FF2D75] mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
